<script setup lang="ts">
import { CARD_COLORS } from '@/enums/common'
import { twMerge } from 'tailwind-merge'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useScreenSize } from '@/composables/helpers/screen-size'

const props = withDefaults(defineProps<{
	color?: CARD_COLORS
	disabled?: boolean
	isLarge?: boolean
	cardHeight?: number
	outerHeight?: number
	cardClass?: string
}>(), {
  color: CARD_COLORS.PINK,
  disabled: false,
  isLarge: false,
  cardHeight: undefined,
  outerHeight: undefined,
  cardClass: '',
})

const emit = defineEmits<{
	(e: 'heightChange', height: number): void
}>()

const { width } = useScreenSize()

const CARD_WIDTH = computed(() => props.isLarge ? 396 : 184)
const CARD_HEIGHT = computed(() => props.cardHeight ?? 246)
const ASPECT_RATIO = computed(() => CARD_HEIGHT.value / CARD_WIDTH.value) // 236 / 184

const cardContainer = ref<HTMLElement | null>(null)
const scaleValue = ref(1)
const calculatedHeight = ref(CARD_HEIGHT.value)

function updateScale() {
  const container = cardContainer.value
  if (!container) return

  const parentWidth = container.offsetWidth

  scaleValue.value = parentWidth / CARD_WIDTH.value

  calculatedHeight.value = parentWidth * ASPECT_RATIO.value
}

// Watch screen size changes
watch(width, () => {
  updateScale()
})

// Initial scale calculation on mount
onMounted(async () => {
  await nextTick()
  updateScale()
})

watch(calculatedHeight, calculatedHeight => {
  emit('heightChange', calculatedHeight)
}, { immediate: true })
</script>
<template>
  <div
    ref="cardContainer"
    class="w-full h-full flex justify-center items-center"
    :class="{'pointer-events-none': disabled}"
    :style="{ height: `${props.outerHeight ?? calculatedHeight}px` }"
  >
    <div
      :style="{ transform: `scale(${scaleValue})` }"
    >
      <div
        :class="twMerge('card p-2 bg-center bg-no-repeat bg-cover justify-between rounded-[20px] relative',
                        !isLarge && 'w-[184px]',
                        isLarge && 'w-[396px]',
                        disabled && 'opacity-[76%] pointer-events-none',
                        (color === CARD_COLORS.PINK) && 'pink-card',
                        (color === CARD_COLORS.BLUE) && 'blue-card',
                        (color === CARD_COLORS.GREEN) && 'green-card',
                        (color === CARD_COLORS.YELLOW) && 'yellow-card',
                        (color === CARD_COLORS.ORANGE) && 'orange-card',
                        (color === CARD_COLORS.GRAY) && 'gray-card',
                        cardClass
        )"
        :style="{ height: `${CARD_HEIGHT}px` }"
      >
        <div class="flex flex-col items-center h-full inner-border relative">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>

.card::before {
	content: "";
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	border-radius: inherit; /* Ensure the same 12px border-radius */
	padding: 2px; /* Add padding to make the border visible */

	/* Initial gradient state */
	background: linear-gradient(90deg, rgba(255, 255, 255, 0.22) 0%, #FFFFFF 49.83%, rgba(255, 255, 255, 0.55) 100%);
	opacity: 1; /* Initial opacity */

	/* Clip to border only */
	-webkit-mask: linear-gradient(white 0 0) content-box, linear-gradient(white 0 0);
	mask: linear-gradient(white 0 0) content-box,
	linear-gradient(white 0 0);

	-webkit-mask-composite: xor;
	mask-composite: exclude;

	pointer-events: none; /* Allow interactions with the content */
	z-index: 1; /* Ensure it sits above the image but below the content */
}

.pink-card {
	background-image: url('@/assets/images/prize-cards/pink-card-background.png');
}

.blue-card {
	background-image: url('@/assets/images/prize-cards/blue-card-background.png');
}

.green-card {
	background-image: url('@/assets/images/prize-cards/green-card-background.png');
}

.yellow-card {
	background-image: url('@/assets/images/prize-cards/yellow-card-background.png');
}

.orange-card {
	background-image: url('@/assets/images/prize-cards/orange-card-background.png');
}

.gray-card {
	background-image: url('@/assets/images/prize-cards/shop-card-background.png');
}

.inner-border::before {
	content: "";
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	border-radius: 20px; /* Ensure the same 12px border-radius */
	padding: 1px; /* Add padding to make the border visible */

	/* Initial gradient state */
	background: radial-gradient(
		ellipse at center,
		rgba(255, 255, 255, 0.8),
		rgba(255, 255, 255, 0.6) 40%,
		rgba(255, 255, 255, 0.2) 70%,
		transparent 85%
	);
	opacity: 1; /* Initial opacity */

	/* Clip to border only */
	-webkit-mask: linear-gradient(white 0 0) content-box, linear-gradient(white 0 0);
	mask: linear-gradient(white 0 0) content-box,
	linear-gradient(white 0 0);

	-webkit-mask-composite: xor;
	mask-composite: exclude;

	pointer-events: none; /* Allow interactions with the content */
	z-index: 1; /* Ensure it sits above the image but below the content */
}
</style>
